import clsx from 'clsx';
import { uniqueId } from 'lodash';
import React, { useCallback } from 'react';

import chevronDown from 'images/chevron_down_icon.svg';

import { useCloseElOnOutsideClick } from '../../../hooks';
import navLinkStyles from '../NavLinks/styles.module.scss';
import styles from './styles.module.scss';

interface Option {
  title: string;
  url: string;
}

export default function NavDropdown({
  dropdownShown,
  highlighted = false,
  inSideNav,
  mainOptions,
  otherOptions = [],
  optionsDividerText,
  setDropdownShown,
  title,
}: {
  dropdownShown: string;
  highlighted?: boolean;
  inSideNav: boolean;
  mainOptions: Option[];
  otherOptions?: Option[];
  optionsDividerText?: string;
  setDropdownShown: React.Dispatch<React.SetStateAction<string>>;
  title: string;
}) {
  const dropdownIdentifier = inSideNav ? `side${title}` : title;
  const isOpen = dropdownShown === dropdownIdentifier;
  const closeDropdown = useCallback(() => setDropdownShown(''), [setDropdownShown]);

  const { ref: dropdownRef } = useCloseElOnOutsideClick({ closeFn: closeDropdown, isOpen });

  const toggleDropdown = () => {
    isOpen ? closeDropdown() : setDropdownShown(dropdownIdentifier);
  };

  return (
    <div className={clsx({ [styles.relativeContainer]: !inSideNav })} ref={dropdownRef}>
      <div
        className={clsx(styles.dropdownOpener, navLinkStyles.navLink)}
        onClick={toggleDropdown}
        data-test={`nav-dropdown-${title}`}
      >
        <span
          className={clsx(styles.dropdownText, {
            'text-pro-red': highlighted,
          })}
        >
          {title}
        </span>
        <img
          src={chevronDown}
          width="10"
          className={clsx(styles.chevronDown, {
            [styles.dropdownClosed]: !isOpen && !highlighted,
            [styles.dropdownOpen]: isOpen,
            [styles.highlightedDropdown]: highlighted && !isOpen,
            [styles.rotateNinety]: inSideNav,
          })}
        />
      </div>
      {mainOptions.length > 0 && (
        <div
          className={clsx(styles.dropdown, {
            [styles.show]: isOpen,
            [styles.sideNavDropdown]: inSideNav,
          })}
        >
          {mainOptions.map((option) => (
            <span
              key={uniqueId()}
              className={clsx(styles.dropdownLink, {
                [styles.boldenedLink]: otherOptions.length > 0,
              })}
            >
              <a href={option.url}>{option.title}</a>
            </span>
          ))}
          {optionsDividerText && (
            <div className={styles.optionsDividerWrapper}>
              <div className={styles.optionsDividerText}>{optionsDividerText}</div>
              <div className={styles.optionsDivider} />
            </div>
          )}
          {otherOptions.length > 0 && (
            <>
              {otherOptions.map((option) => (
                <span key={uniqueId()} className={styles.dropdownLink}>
                  <a href={option.url}>{option.title}</a>
                </span>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
